/* =========================================================================
   Lightweight activity telemetry → batched POST. Powers the /sadmin "time on
   site" metrics: screen views + heartbeats while the player is actually
   interacting (CODEX §26 rule: heartbeats stop after ~2 minutes of no
   interaction and resume on the next touch/keypress).

   Tracking now runs for EVERYONE: flush() routes authenticated batches through
   /api/log/activity (Bearer) and guest batches through /api/log/guest-activity
   (device guest id, no Bearer). Guest heartbeats/screen-views feed the admin
   "active guests/day", unique-IP and live-now views. Bounded queue + single
   retry semantics are preserved.

   DURABILITY (Part A). A guest who force-quits the PWA sub-second after boot
   used to be lost entirely: events only ever lived in memory and were flushed
   every 30s / on visibilitychange. Three reinforcements now close that gap:
     1. Early first flush — the FIRST track() (ScreenView{welcome}, which fires
        AFTER auth restore resolves so it's correctly attributed) schedules a
        one-shot 1s flush. The 30s cadence is otherwise unchanged.
     2. Teardown flush — flushOnHide() runs on BOTH window 'pagehide' and the
        existing document visibilitychange→hidden. Guests go out via sendBeacon
        (survives the kill without keeping the page alive); authed users still
        use fetch keepalive (sendBeacon can't carry the Bearer header).
     3. Queue persistence — the queue is write-through mirrored to localStorage
        (`catholics.telemetryQueue`) after every mutation and replayed on the
        next boot, so an event queued right before a kill is delivered later.

   Two accepted quirks, both strict improvements over the old always-lose path:
     (a) If a guest batch is handed to sendBeacon and the OS silently drops it,
         those events are gone — but the old code lost 100% of a pre-flush kill,
         so this is upside only.
     (b) Guest events persisted on one boot but only replayed on a LATER boot
         where the device has since signed in attribute to that user, not the
         guest id. Same human, so analytics-fine.
   ========================================================================= */
import {
  isAuthenticated,
  postActivity,
  postGuestActivity,
  beaconGuestActivity,
  type ActivityEvent,
} from './api';
import { getGuestId } from './guestId';
import type { KVStore } from './dailyBlessing';

const FLUSH_MS = 30_000;        // batch flush cadence
const FIRST_FLUSH_MS = 1_000;   // one-shot flush after the very first track() (guest-kill durability)
const HEARTBEAT_MS = 60_000;    // active-time tick
const IDLE_CUTOFF_MS = 120_000; // stop heartbeats after 2 min without interaction
const MAX_QUEUE = 50;           // also the server's Take(50) cap on a batch

/* Persisted queue mirror. The `catholics.` prefix is load-bearing: the ?fresh
   factory reset in main.ts wipes every `catholics.*` key by prefix, so a reset
   device starts with an empty telemetry queue automatically (no bespoke wipe). */
const QUEUE_KEY = 'catholics.telemetryQueue';

let queue: ActivityEvent[] = [];
let lastInteraction = Date.now();
let currentSessionId: string | null = null;
let firstFlushScheduled = false; // arm the 1s durability flush exactly once per boot

/** Storage seam identical to dailyBlessing/guestId: any localStorage touch can
    throw in private/locked-down contexts, so every access is guarded and a null
    store degrades to today's in-memory-only behavior. */
function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/* -------------------------- pure queue helpers ---------------------------
   Extracted so the persistence contract is unit-testable without a DOM. */

/** Cap-aware append: at MAX_QUEUE we drop the OLDEST event so the queue never
    grows unbounded (mirrors the old `queue.shift()` on overflow). Returns a new
    array — callers reassign, keeping mutations funneled through persistQueue. */
export function appendCapped(events: ActivityEvent[], event: ActivityEvent): ActivityEvent[] {
  const next = events.length >= MAX_QUEUE ? events.slice(1) : events.slice();
  next.push(event);
  return next;
}

/** Merge a failed batch back AHEAD of anything queued while the POST was in
    flight, capped to the newest MAX_QUEUE (mirrors flush's requeue). */
export function mergeRequeue(batch: ActivityEvent[], current: ActivityEvent[]): ActivityEvent[] {
  return batch.concat(current).slice(-MAX_QUEUE);
}

/** Normalize an already-parsed replay value into a safe queue. Defensive: a
    non-array is discarded entirely, individual items must carry a string
    eventKey, and we keep only the last MAX_QUEUE (matches the server's Take). */
export function normalizeQueue(raw: unknown): ActivityEvent[] {
  if (!Array.isArray(raw)) return [];
  const kept = raw.filter(
    (e): e is ActivityEvent =>
      !!e && typeof e === 'object' && typeof (e as { eventKey?: unknown }).eventKey === 'string',
  );
  return kept.slice(-MAX_QUEUE);
}

/** Parse a persisted queue string. A parse error discards the whole payload
    (returns []) rather than throwing — a corrupt mirror never blocks boot. */
export function parseTelemetryQueue(rawJson: string | null): ActivityEvent[] {
  if (rawJson === null) return [];
  try {
    return normalizeQueue(JSON.parse(rawJson));
  } catch {
    return [];
  }
}

/** Write-through the current queue to the store. Called after EVERY mutation
    (track push, flush take, requeue). Guarded so private mode is a silent no-op
    that leaves us at today's in-memory behavior. */
export function persistQueueTo(events: ActivityEvent[], store: KVStore | null): void {
  if (!store) return;
  try {
    store.setItem(QUEUE_KEY, JSON.stringify(events));
  } catch {
    /* storage unavailable — degrade to in-memory (same as guestId.ts) */
  }
}

/** Replay a persisted queue from the store, normalized. A throwing/absent store
    yields an empty queue (no boot-time send is scheduled here — the first
    track() every boot arms the 1s flush post-auth). */
export function replayQueueFrom(store: KVStore | null): ActivityEvent[] {
  if (!store) return [];
  let raw: string | null = null;
  try {
    raw = store.getItem(QUEUE_KEY);
  } catch {
    return [];
  }
  return parseTelemetryQueue(raw);
}

/** Module-bound write-through of the live queue. */
function persistQueue(): void {
  persistQueueTo(queue, deviceStore());
}

/* One central observer seam: the quest engine registers here so EVERY existing
   track() call site feeds quest evaluation with zero per-site edits (house
   pattern, mirrors dom.ts's setOnScreenShown). Kept as a nullable sink rather
   than a direct import to avoid a telemetry↔quests module cycle; the observer
   is fired synchronously after queuing and must never throw. */
let trackObserver: ((eventKey: string, props?: Record<string, unknown>) => void) | null = null;
export function setTrackObserver(fn: ((eventKey: string, props?: Record<string, unknown>) => void) | null): void {
  trackObserver = fn;
}

export function setTelemetrySession(sessionId: string | null): void {
  currentSessionId = sessionId;
}

export function track(eventKey: string, props?: Record<string, unknown>): void {
  // Guests and signed-in players both queue — flush() decides the destination.
  queue = appendCapped(queue, {
    sessionId: currentSessionId,
    eventKey,
    propsJson: props ? JSON.stringify(props) : null,
    clientUtc: new Date().toISOString(),
  });
  persistQueue(); // write-through so a kill before the next flush isn't total loss

  // First event of the boot (ScreenView{welcome}, fired AFTER auth restore so
  // it's attributed correctly) arms a one-shot early flush — a fast guest quit
  // is otherwise only caught at the 30s cadence.
  if (!firstFlushScheduled) {
    firstFlushScheduled = true;
    setTimeout(() => void flush(), FIRST_FLUSH_MS);
  }

  // Forward to quest evaluation (and anything else that subscribes). Never let a
  // subscriber error break telemetry.
  if (trackObserver) {
    try {
      trackObserver(eventKey, props);
    } catch {
      /* observer must not affect telemetry */
    }
  }
}

async function flush(useKeepalive = false): Promise<void> {
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  persistQueue(); // clear-at-take: persist the emptied queue BEFORE awaiting the POST
  const ok = isAuthenticated()
    ? await postActivity(batch, useKeepalive)
    : await postGuestActivity(getGuestId(), batch, useKeepalive);
  // Requeue AHEAD of anything tracked while the POST was in flight — the old
  // `queue.length === 0` guard silently dropped a failed batch whenever a
  // heartbeat/ScreenView landed mid-flight.
  if (!ok) {
    queue = mergeRequeue(batch, queue);
    persistQueue();
  }
}

/** Teardown flush shared by pagehide + visibilitychange→hidden. Both entry
    points take the queue synchronously, so a double-fire (hidden THEN pagehide)
    is safe: the second call sees an empty queue and returns immediately. */
function flushOnHide(): void {
  // Authed: fetch keepalive is mandatory — only it can carry the Bearer header,
  // which sendBeacon cannot. flush() has its own empty-queue early return.
  if (isAuthenticated()) {
    void flush(true);
    return;
  }
  // Guest: take the queue synchronously (empty it + persist the empty state),
  // then hand it to sendBeacon which survives the kill without keeping the page
  // alive. On refusal (unsupported/blocked) requeue + persist and fall back to
  // the fetch keepalive path.
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  persistQueue();
  const sent = beaconGuestActivity(getGuestId(), batch);
  if (!sent) {
    queue = mergeRequeue(batch, queue);
    persistQueue();
    void flush(true);
  }
}

export function initTelemetry(): void {
  // Replay any queue a prior page load persisted (delivers events that were
  // queued right before a kill). Re-persist the normalized form so a corrupt or
  // oversized mirror is cleaned up immediately. NB: no flush is scheduled here —
  // init runs BEFORE await tryRestoreSession() (main.ts), so a boot-time send
  // would race auth restore and mislabel returning signed-in users as guests.
  // The first track() (post-auth) arms the 1s flush instead.
  queue = replayQueueFrom(deviceStore());
  persistQueue();

  // interaction tracking for the active-time rule
  for (const evt of ['pointerdown', 'keydown'] as const) {
    document.addEventListener(evt, () => { lastInteraction = Date.now(); }, { passive: true });
  }
  // heartbeat while active
  setInterval(() => {
    if (Date.now() - lastInteraction <= IDLE_CUTOFF_MS) track('Heartbeat');
  }, HEARTBEAT_MS);
  // periodic flush + final flush when the app is backgrounded/closed. pagehide
  // AND visibilitychange are both wired: iOS/PWA kills often deliver only one.
  setInterval(() => void flush(), FLUSH_MS);
  window.addEventListener('pagehide', flushOnHide);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnHide();
  });
}
